import decode from "jwt-decode";
import { Base } from "./Base";
import { throwExpected, NotFoundException, ForbiddenException, UsernameAlreadyExistsException, UnprocessableEntityException, UserNameNotFoundException, LensSDKException, TokenNotFoundException, SubscriptionAlreadyExistsException, BadRequestException, UnauthorizedException, } from "./exceptions";
/**
 *
 * The class for consuming all `user` resources.
 *
 * currently we have:
 * - `.getOne` maps to `GET /users/{username}`
 * - `.getBillingPageToken` maps to `GET /users/{username}/billing-page-token`
 *
 * @remarks
 * This class should be generated using OpenAPI in the future.
 *
 * @alpha
 */
class UserService extends Base {
    async getUsername() {
        const token = await this.lensPlatformClient.getDecodedAccessToken();
        if (!(token === null || token === void 0 ? void 0 : token.preferred_username)) {
            throw new LensSDKException(null, "no access token or no preferred_username");
        }
        return token.preferred_username;
    }
    async getOne({ username }, queryString) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}${queryString ? `?${queryString}` : ""}`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new NotFoundException(`User ${username} not found`),
        });
        return json;
    }
    async getMany(queryString) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users${queryString ? `?${queryString}` : ""}`;
        const json = await throwExpected(async () => fetch.get(url));
        return json;
    }
    /**
     * Update user
     */
    async updateOne(username, user) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}`;
        const json = await throwExpected(async () => fetch.patch(url, user), {
            404: () => new NotFoundException(`User ${username} not found`),
            403: () => new ForbiddenException(`Modification of user ${username} is forbidden`),
            409: () => new UsernameAlreadyExistsException(),
        });
        return json;
    }
    async getSelf() {
        const decodedAccessToken = await this.lensPlatformClient.getDecodedAccessToken();
        if (decodedAccessToken === null || decodedAccessToken === void 0 ? void 0 : decodedAccessToken.preferred_username) {
            const json = await this.getOne({ username: decodedAccessToken === null || decodedAccessToken === void 0 ? void 0 : decodedAccessToken.preferred_username });
            return json;
        }
        throw new Error(`jwt.preferred_username is ${decodedAccessToken === null || decodedAccessToken === void 0 ? void 0 : decodedAccessToken.preferred_username}`);
    }
    async deleteOne(username) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}`;
        await throwExpected(async () => fetch.delete(url), {
            500(error) {
                const message = error === null || error === void 0 ? void 0 : error.body.message;
                if (typeof message === "string") {
                    if (message.includes("Token")) {
                        return new TokenNotFoundException();
                    }
                    if (message.includes("User")) {
                        return new UserNameNotFoundException(username);
                    }
                }
                return new LensSDKException(500, `Unexpected exception [Lens Platform SDK]: ${error === null || error === void 0 ? void 0 : error.body.message}`, error);
            },
            404: () => new UserNameNotFoundException(username),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
    }
    async getUserSubscriptions(username) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/subscriptions`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Access to user ${username} is forbidden`),
        });
        return json;
    }
    async getUserSubscriptionsSeats(username) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/subscription-seats`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Access to user ${username} is forbidden`),
        });
        return json;
    }
    async getUserSubscription(username, subscriptionId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/subscriptions/${subscriptionId}`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Access to user ${username} is forbidden`),
        });
        return json;
    }
    async activateSubscription({ username, license, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/subscriptions`;
        const json = await throwExpected(async () => fetch.post(url, license), {
            404(error) {
                const message = error === null || error === void 0 ? void 0 : error.body.message;
                if (typeof message === "string") {
                    if (message.includes("User")) {
                        return new UserNameNotFoundException(username);
                    }
                }
                return new NotFoundException(`Recurly subscription ${license.subscriptionId} not found`);
            },
            409: (error) => {
                var _a;
                return new SubscriptionAlreadyExistsException((_a = error === null || error === void 0 ? void 0 : error.body.message) !== null && _a !== void 0 ? _a : `Subscription for user ${username} already exists`);
            },
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Modification of user licenses for ${username} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    async activateSubscriptionSeat({ username, license, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/subscription-seats`;
        const json = await throwExpected(async () => fetch.post(url, license), {
            404(error) {
                const message = error === null || error === void 0 ? void 0 : error.body.message;
                if (typeof message === "string") {
                    if (message.includes("User")) {
                        return new UserNameNotFoundException(username);
                    }
                }
                return new NotFoundException(`Recurly subscription ${license.subscriptionId} not found`);
            },
            409: (error) => {
                var _a;
                return new SubscriptionAlreadyExistsException((_a = error === null || error === void 0 ? void 0 : error.body.message) !== null && _a !== void 0 ? _a : `Subscription seat for user ${username} already exists`);
            },
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Modification of user licenses for ${username} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    async getSubscriptionSeatOfflineActivationCode({ username, subscriptionSeatId, activationCodeData, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/subscription-seats/${subscriptionSeatId}/activation-code`;
        const json = await throwExpected(async () => fetch.post(url, activationCodeData), {
            404(error) {
                const message = error === null || error === void 0 ? void 0 : error.body.message;
                if (typeof message === "string") {
                    if (message.includes("User")) {
                        return new UserNameNotFoundException(username);
                    }
                }
                return new NotFoundException(`Subscription seat ${subscriptionSeatId} not found`);
            },
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Modification of user licenses for ${username} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    async deactivateSubscription({ username, license, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/subscriptions/${license.subscriptionId}`;
        await throwExpected(async () => fetch.delete(url), {
            404(error) {
                const message = error === null || error === void 0 ? void 0 : error.body.message;
                if (typeof message === "string") {
                    if (message.includes("User")) {
                        return new UserNameNotFoundException(username);
                    }
                }
                return new NotFoundException(`Recurly subscription ${license.subscriptionId} not found`);
            },
            403: () => new ForbiddenException(`Modification of user licenses for ${username} is forbidden`),
            400: () => new BadRequestException(),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
    }
    async deactivateSubscriptionSeat({ username, license, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/subscription-seats/${license.subscriptionId}`;
        await throwExpected(async () => fetch.delete(url), {
            404(error) {
                const message = error === null || error === void 0 ? void 0 : error.body.message;
                if (typeof message === "string") {
                    if (message.includes("User")) {
                        return new UserNameNotFoundException(username);
                    }
                }
                return new NotFoundException(`Recurly subscription ${license.subscriptionId} not found`);
            },
            403: () => new ForbiddenException(`Modification of user licenses for ${username} is forbidden`),
            400: () => new BadRequestException(),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
    }
    async getBillingPageToken(username) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/billing-page-token`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new NotFoundException(`User ${username} not found`),
            403: () => new ForbiddenException(`Getting the billing page token for ${username} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    async getBillingPageTokenBySubscriptionId(username, subscriptionId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/subscriptions/${subscriptionId}/billing-page-token`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new NotFoundException(`User ${username} not found`),
            403: () => new ForbiddenException(`Getting the billing page token for ${username} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    async getUserBillingInformation(username) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/billing`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new NotFoundException(`User ${username} not found`),
            403: () => new ForbiddenException(`Getting the billing information for ${username} is forbidden`),
        });
        return json;
    }
    async getUserInvoices(username) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/invoices`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new NotFoundException(`User ${username} not found`),
            403: () => new ForbiddenException(`Getting the invoices for ${username} is forbidden`),
        });
        return json;
    }
    /**
     * Get all emails (primary+secondary) of the user
     */
    async getEmails() {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const username = await this.getUsername();
        const url = `${apiEndpointAddress}/users/${username}/emails`;
        const json = await throwExpected(async () => fetch.get(url), {
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Add new secondary email(s) to user's account, newly added email will be unverified
     * and can be promoted to primary later verification.
     */
    async createEmails(emails) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const username = await this.getUsername();
        const url = `${apiEndpointAddress}/users/${username}/emails`;
        const json = await throwExpected(async () => fetch.post(url, emails), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Upload user avatar
     * @param avatar: HTML form file input value
     */
    async uploadAvatar(avatar) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const username = await this.getUsername();
        const url = `${apiEndpointAddress}/users/${username}/avatar`;
        const formData = new FormData();
        formData.append("avatar", avatar);
        formData.append("fileName", avatar.name);
        const json = await throwExpected(async () => fetch.post(url, formData), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            413: () => new BadRequestException("Image size is too big. Image size should be less than 3MB."),
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Delete secondary email(s) from user account
     */
    async deleteEmails(emails) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const username = await this.getUsername();
        const url = `${apiEndpointAddress}/users/${username}/emails`;
        await throwExpected(async () => fetch.delete(url, { data: emails }), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
    }
    /**
     * Send a verification email to a user's unverified secondary email address
     */
    async sendVerificationEmail(email) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const username = await this.getUsername();
        const url = `${apiEndpointAddress}/users/${username}/emails/send-verification`;
        await throwExpected(async () => fetch.put(url, { email }), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
    }
    /**
     * Verify a user's secondary email using the token
     */
    async verifySecondaryEmail(token) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const decoded = decode(token);
        if (!decoded.username) {
            throw new LensSDKException(null, "Invalid token");
        }
        const url = `${apiEndpointAddress}/users/${decoded.username}/emails/verification`;
        await throwExpected(async () => fetch.post(url, { token }), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
    }
    /**
     * Set a verified email as user's primary email
     */
    async setPrimaryEmail(email) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const username = await this.getUsername();
        const url = `${apiEndpointAddress}/users/${username}/emails`;
        await throwExpected(async () => fetch.patch(url, { email }), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
    }
    async confirmPersonalLicenseEligibility(username) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/users/${username}/confirm-personal-eligibility`;
        await throwExpected(async () => fetch.post(url, { username }), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: () => new NotFoundException(`User ${username} not found`),
        });
    }
    getUserFullName(user) {
        const { firstName, lastName, fullname, username } = user;
        if (firstName && lastName) {
            return `${firstName} ${lastName}`;
        }
        if (firstName) {
            return firstName;
        }
        if (fullname) {
            return fullname;
        }
        return username !== null && username !== void 0 ? username : "";
    }
}
export { UserService };
